/**
 * @fileoverview Shared color and dimensional primitives for authored artwork.
 * @module ui-artwork/svg/shared
 *
 * Responsibilities:
 * - Derive light and dark face colors from a validated catalog color.
 * - Provide the common ground-shadow treatment used by dimensional families.
 *
 * Dependencies: None.
 * Architecture: Pure SVG-string helpers; no assets, DOM, or network access.
 * Notes: Inputs are normalized by the artwork color policy before rendering.
 */

/**
 * @reusable shadeColor
 * Usage: Derive a light or dark face color from a normalized six-digit hex color.
 * Rules: Invalid input uses the package's neutral blue base; output is always six-digit hex.
 */
export function shadeColor(hex: string, amount: number): string {
  const value = /^#[\da-f]{6}$/i.test(hex) ? hex.slice(1) : '2563eb';
  const channels = [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
  return `#${channels.map((channel) => Math.round(channel + ((amount > 0 ? 255 : 0) - channel) * Math.abs(amount)).toString(16).padStart(2, '0')).join('')}`;
}

/** @reusable Shared ground-shadow ellipse for authored dimensional bodies. */
export const GROUND_SHADOW = '<ellipse cx="48" cy="85" rx="34" ry="6" fill="#0f172a" opacity=".18"/>';

/**
 * @reusable isoBlock
 * Usage: Compose a dimensional body from top, side, and front faces.
 * Rules: The caller supplies subject details; this helper only provides shaded faces and shadow.
 */
export function isoBlock(
  color: string,
  front: string,
  side: string,
  roof: string,
): string {
  const light = shadeColor(color, 0.42);
  const dark = shadeColor(color, -0.42);
  return `${GROUND_SHADOW}<path d="M18 29 51 12l29 15-34 18z" fill="${light}"/><path d="m46 45 34-18v39L46 84z" fill="${dark}"/><path d="M18 29 46 45v39L18 68z" fill="${color}"/>${front}${side}${roof}`;
}

/**
 * @reusable framedSymbol
 * Usage: Create a NAPSG-style framed operational symbol with the selected border geometry.
 * Rules: Frame geometry is restricted to hexagon, diamond, or circle.
 */
export function framedSymbol(color: string, content: string, frame = 'hexagon'): string {
  const outline = frame === 'diamond'
    ? '<path d="m48 7 41 41-41 41L7 48z"/>'
    : frame === 'circle'
      ? '<circle cx="48" cy="48" r="40"/>'
      : '<path d="M27 8h42l20 20v40L69 88H27L7 68V28z"/>';
  return `<g fill="#fff" stroke="${color}" stroke-width="5" stroke-linejoin="round">${outline}</g><g fill="none" stroke="#1e293b" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">${content}</g>`;
}
